import React, { useState } from 'react';
import api from '../utils/apiClient';

const AddCompetitorModal = ({ isOpen, onClose, onAdd, loading }) => {
  const [newCompetitorUrl, setNewCompetitorUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [checkingUrl, setCheckingUrl] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [validatedUrl, setValidatedUrl] = useState('');
  const [warningMessage, setWarningMessage] = useState('');

  const validateUrlFormat = (url) => {
    try {
      // Prepend https:// if no protocol is specified
      if (!/^https?:\/\//i.test(url)) {
        url = `https://${url}`;
      }
      
      // Check if URL is valid format
      new URL(url);
      return url;
    } catch (e) {
      return null;
    }
  };

  const checkUrlAccessibility = async (url) => {
    setCheckingUrl(true);
    try {
      // Use the server-side endpoint to check URL accessibility
      const response = await api.checkUrlAccessibility(url);
      return {
        isAccessible: response.data.isAccessible,
        message: response.data.message || 'URL may not be accessible'
      };
    } catch (err) {
      console.error('Error checking URL accessibility:', err);
      return { 
        isAccessible: false, 
        message: err.response?.data?.error || err.message || 'Could not verify URL accessibility'
      };
    } finally {
      setCheckingUrl(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUrlError('');
    
    if (!newCompetitorUrl.trim() || loading || checkingUrl) return;
    
    const formattedUrl = validateUrlFormat(newCompetitorUrl.trim());
    
    if (!formattedUrl) {
      setUrlError('Please enter a valid website URL');
      return;
    }

    // If we're already showing a warning and user proceeds, submit anyway
    if (showWarning && validatedUrl === formattedUrl) {
      onAdd(formattedUrl);
      return;
    }

    setCheckingUrl(true);
    
    try {
      // Check if the URL is likely to be accessible
      const { isAccessible, message } = await checkUrlAccessibility(formattedUrl);

      if (!isAccessible) {
        setWarningMessage(message);
        setShowWarning(true);
        setValidatedUrl(formattedUrl);
        return;
      }
      
      // URL is accessible, proceed with adding
      onAdd(formattedUrl);
    } catch (err) {
      console.error('Error validating URL:', err);
      // If validation fails, show warning
      setWarningMessage('Could not verify URL accessibility');
      setShowWarning(true);
      setValidatedUrl(formattedUrl);
    } finally {
      setCheckingUrl(false);
    }
  };

  const handleAddAnyway = () => {
    if (validatedUrl) {
      onAdd(validatedUrl);
    }
    setShowWarning(false);
  };

  const handleCancel = () => {
    setShowWarning(false);
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Add Competitor</h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-500"
            disabled={loading || checkingUrl}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {showWarning ? (
          <div>
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Warning: URL may not be accessible</h3>
                  <div className="mt-1 text-sm text-yellow-700">
                    <p>The URL <strong>{validatedUrl}</strong> doesn't appear to be accessible.</p>
                    <p className="mt-1">{warningMessage}</p>
                    <p className="mt-2">This might happen if:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>The website is down or not publicly accessible</li>
                      <li>The domain doesn't exist</li>
                      <li>There's a typo in the URL</li>
                    </ul>
                    <p className="mt-2">Do you want to add it anyway?</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowWarning(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                disabled={loading}
              >
                Edit URL
              </button>
              <button
                type="button"
                onClick={handleAddAnyway}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Adding...</span>
                  </div>
                ) : (
                  'Add Anyway'
                )}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="competitorUrl" className="block text-sm font-medium text-gray-700 mb-2">
                Competitor URL
              </label>
              <input
                type="text"
                id="competitorUrl"
                value={newCompetitorUrl}
                onChange={(e) => {
                  setNewCompetitorUrl(e.target.value);
                  setUrlError('');
                }}
                placeholder="Enter competitor domain (e.g., example.com)"
                className={`w-full px-4 py-2 border ${urlError ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
                disabled={loading || checkingUrl}
              />
              {urlError && (
                <p className="mt-1 text-sm text-red-600">{urlError}</p>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                disabled={loading || checkingUrl}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!newCompetitorUrl.trim() || loading || checkingUrl}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 w-32"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Adding...</span>
                  </>
                ) : checkingUrl ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Checking...</span>
                  </>
                ) : (
                  'Add Competitor'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddCompetitorModal; 